import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Send, AlertCircle, Calendar, Users, Bell, Sparkles } from 'lucide-react'
import { announcementApi } from '../services/api'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { extractErrorMessage } from '../utils/errorUtils'
import { showError } from '../utils/showError'
import AIChatPanel from '../components/AIChatPanel'
import {
  PageHeader,
  LoadingSpinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormField,
  FormSelect,
  FormTextarea,
  FormCheckbox,
  FormGroup,
  Badge,
  ErrorMessage,
  Modal,
  DetailPageLayout
} from '../components/common'

const MEMBER_PAGE_SIZE = 50
// Matches the building switcher in the top bar, so the two search boxes feel the same.
const MEMBER_SEARCH_DEBOUNCE_MS = 300

const CreateAnnouncement = () => {
  const navigate = useNavigate()
  const { announcementId } = useParams()
  const { selectedBuildingId, selectedBuildingName } = useAuth()
  const isEditing = !!announcementId

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 2, // Medium priority
    eventDate: '',
    scheduleDate: '',
    isAcknowledgeReq: false,
    notificationMethods: [1], // Email by default
    buildingId: '',
    // Recipient targeting
    recipientType: 'groups', // 'groups' | 'floors' | 'members'
    isAllResidents: true,
    isAllOwners: false,
    isAllStaffs: false,
    selectedFloors: [],
  })
  const [errors, setErrors] = useState({})
  const [availableFloors, setAvailableFloors] = useState([])
  const [memberSearch, setMemberSearch] = useState('')
  const [availableMembers, setAvailableMembers] = useState([])
  const [selectedMembers, setSelectedMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersLoadingMore, setMembersLoadingMore] = useState(false)
  const [membersTotal, setMembersTotal] = useState(null)
  const [membersPage, setMembersPage] = useState(1)
  const [recipientCount, setRecipientCount] = useState(null)
  const [countLoading, setCountLoading] = useState(false)
  const memberSearchDebounceRef = useRef(null)
  // Every members reply carries the generation it was issued under. A fresh search bumps this,
  // so a slow earlier reply cannot land on top of a list the search box no longer describes.
  const memberSearchGenerationRef = useRef(0)

  // AI Chat state
  const [showAIChat, setShowAIChat] = useState(false)

  // AI Revise state
  const [showReviseModal, setShowReviseModal] = useState(false)
  const [reviseLoading, setReviseLoading] = useState(false)
  const [reviseError, setReviseError] = useState(null)

  useEffect(() => {
    if (isEditing) {
      loadAnnouncementForEdit()
    }
  }, [isEditing, announcementId])

  // Sync building from global selector (for new announcements)
  useEffect(() => {
    if (!isEditing && selectedBuildingId) {
      setFormData(prev => ({ ...prev, buildingId: selectedBuildingId }))
    }
  }, [selectedBuildingId, isEditing])

  const loadAnnouncementForEdit = async () => {
    try {
      setLoading(true)
      const response = await announcementApi.get(announcementId)
      const announcement = response.data

      // The API serves the notification methods as announceType; the older name is kept as a
      // fallback so a response using it still restores instead of dropping to Email.
      const savedMethods = announcement.announceType ?? announcement.notificationMethods

      // Merge into the current state rather than replacing it — anything the response does not
      // describe has to keep its default instead of arriving as undefined.
      setFormData(prev => ({
        ...prev,
        title: announcement.title || '',
        content: announcement.content || '',
        priority: announcement.priority || 2,
        eventDate: announcement.eventDate ? announcement.eventDate.slice(0, 16) : '',
        scheduleDate: announcement.scheduleDate ? announcement.scheduleDate.slice(0, 16) : '',
        isAcknowledgeReq: announcement.isAcknowledgeReq || false,
        notificationMethods: savedMethods?.length ? savedMethods : [1],
        buildingId: announcement.buildingId || '',
        // Only the three group flags are stored against an announcement, so groups is the one
        // mode the response can describe. A null flag predates them and keeps the form default;
        // a false one is a real answer and is respected. Floor and member targeting is not
        // returned at all, so an announcement sent that way comes up with nothing selected.
        recipientType: 'groups',
        isAllResidents: announcement.isAllResidents ?? prev.isAllResidents,
        isAllOwners: announcement.isAllOwners ?? prev.isAllOwners,
        isAllStaffs: announcement.isAllStaffs ?? prev.isAllStaffs,
        selectedFloors: [],
      }))
    } catch (error) {
      console.error('Error loading announcement:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less'
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required'
    } else if (formData.content.length > 5000) {
      newErrors.content = 'Content must be 5000 characters or less'
    }

    if (!selectedBuildingId) {
      newErrors.buildingId = 'Please select a building from the top bar'
    }

    if (formData.notificationMethods.length === 0) {
      newErrors.notificationMethods = 'At least one notification method is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleNotificationMethodChange = (method, checked) => {
    setFormData(prev => ({
      ...prev,
      notificationMethods: checked
        ? [...prev.notificationMethods, method]
        : prev.notificationMethods.filter(m => m !== method)
    }))

    if (errors.notificationMethods) {
      setErrors(prev => ({ ...prev, notificationMethods: undefined }))
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setSubmitting(true)

      const submitData = {
        ...formData,
        eventDate: formData.eventDate ? new Date(formData.eventDate).toISOString() : null,
        scheduleDate: formData.scheduleDate ? new Date(formData.scheduleDate).toISOString() : null,
        ...buildRecipientTargeting(),
      }
      // Remove internal-only fields before sending
      delete submitData.recipientType
      delete submitData.selectedFloors

      if (isEditing) {
        await announcementApi.update(announcementId, {
          ...submitData,
          announcementId: parseInt(announcementId)
        })
      } else {
        await announcementApi.create(submitData)
      }

      navigate('/announcements')
    } catch (error) {
      console.error('Error saving announcement:', error)
      showError(error)
    } finally {
      setSubmitting(false)
    }
  }

  // Load available floors when building changes
  useEffect(() => {
    if (formData.buildingId) {
      loadFloors(formData.buildingId)
    }
  }, [formData.buildingId])

  const loadFloors = async (buildingId) => {
    try {
      const response = await api.get(`/Building/${buildingId}/floors`)
      setAvailableFloors(response.data || [])
    } catch {
      setAvailableFloors([])
    }
  }

  // One page of members per request. The list appends rather than replacing, so a building with more
  // members than this stays reachable by scrolling instead of only by knowing a name to search for.
  const searchMembers = async (search, pageNumber = 1) => {
    if (!formData.buildingId) return
    const isFirstPage = pageNumber === 1
    // A fresh search replaces the list, so it supersedes whatever is still in flight. A "load more"
    // only appends, so it takes the current generation without claiming one of its own.
    if (isFirstPage) memberSearchGenerationRef.current += 1
    const generation = memberSearchGenerationRef.current
    const isCurrent = () => generation === memberSearchGenerationRef.current
    if (isFirstPage) setMembersLoading(true)
    else setMembersLoadingMore(true)
    try {
      const response = await api.get(`/Building/${formData.buildingId}/members`, {
        params: { pageNumber, pageSize: MEMBER_PAGE_SIZE, searchString: search || undefined }
      })
      if (!isCurrent()) return
      const items = response.data?.items || []
      setAvailableMembers(prev => (isFirstPage ? items : [...prev, ...items]))
      setMembersTotal(response.data?.totalCount ?? null)
      setMembersPage(pageNumber)
    } catch {
      if (!isCurrent()) return
      // A failed "load more" leaves what is already on screen alone — only a fresh search clears it.
      if (isFirstPage) {
        setAvailableMembers([])
        setMembersTotal(null)
        setMembersPage(1)
      }
    } finally {
      // A superseded reply must not switch off the spinner for the search that replaced it.
      if (isCurrent()) {
        if (isFirstPage) setMembersLoading(false)
        else setMembersLoadingMore(false)
      }
    }
  }

  const cancelPendingMemberSearch = () => {
    if (memberSearchDebounceRef.current) clearTimeout(memberSearchDebounceRef.current)
  }

  useEffect(() => {
    if (formData.recipientType === 'members' && formData.buildingId) {
      // A term half-typed before the manager switched tabs must not fire once they are back.
      cancelPendingMemberSearch()
      searchMembers(memberSearch)
    }
  }, [formData.recipientType, formData.buildingId])

  useEffect(() => cancelPendingMemberSearch, [])

  // Typing is not a request. The search waits until the manager stops, so a six-letter name costs
  // one call rather than six, and the list stops flickering through prefixes on the way.
  const handleMemberSearch = (value) => {
    setMemberSearch(value)
    cancelPendingMemberSearch()
    memberSearchDebounceRef.current = setTimeout(() => searchMembers(value), MEMBER_SEARCH_DEBOUNCE_MS)
  }

  const handleLoadMoreMembers = () => {
    searchMembers(memberSearch, membersPage + 1)
  }

  // A count calculated against an older selection is worse than no count, so drop it as soon as the
  // targeting moves and make the manager ask again.
  useEffect(() => {
    setRecipientCount(null)
  }, [
    formData.recipientType,
    formData.buildingId,
    formData.isAllResidents,
    formData.isAllOwners,
    formData.isAllStaffs,
    formData.selectedFloors,
    selectedMembers,
  ])

  const toggleMember = (member) => {
    setSelectedMembers(prev => {
      const exists = prev.find(m => m.userId === member.userId)
      if (exists) return prev.filter(m => m.userId !== member.userId)
      return [...prev, member]
    })
  }

  // The targeting the announcement is actually sent with. The preview and the submit both build their
  // payload from here, so the number shown before sending cannot drift from who ends up receiving it.
  const buildRecipientTargeting = () => ({
    buildingId: formData.buildingId,
    isAllResidents: formData.recipientType === 'groups' ? formData.isAllResidents : false,
    isAllOwners: formData.recipientType === 'groups' ? formData.isAllOwners : false,
    isAllStaffs: formData.recipientType === 'groups' ? formData.isAllStaffs : false,
    floors: formData.recipientType === 'floors' ? formData.selectedFloors : [],
    unitIds: formData.recipientType === 'members'
      ? selectedMembers.filter(m => !m.isStaff).map(m => m.unitId).filter(Boolean)
      : [],
    staffUserIds: formData.recipientType === 'members'
      ? selectedMembers.filter(m => m.isStaff).map(m => m.userId)
      : [],
  })

  // The server resolves the audience the same way it does when sending, so this is the real reach and
  // not an estimate over whichever members happened to fit in one page.
  const calculateRecipients = async () => {
    if (!formData.buildingId) return
    setCountLoading(true)
    try {
      const response = await announcementApi.previewRecipients(buildRecipientTargeting())
      const data = response.data || {}
      setRecipientCount({
        total: data.total ?? 0,
        residents: data.residents ?? 0,
        owners: data.owners ?? 0,
        staff: data.staff ?? 0,
      })
    } catch {
      setRecipientCount(null)
    } finally {
      setCountLoading(false)
    }
  }

  const handleFloorToggle = (floor) => {
    setFormData(prev => ({
      ...prev,
      selectedFloors: prev.selectedFloors.includes(floor)
        ? prev.selectedFloors.filter(f => f !== floor)
        : [...prev.selectedFloors, floor]
    }))
  }

  const handleAIApply = (announcementData) => {
    const d = announcementData
    const priorityMap = { 1: 1, 2: 2, 3: 3 }
    const methods = []
    if (d.email) methods.push(1)
    if (d.sms) methods.push(2)
    if (methods.length === 0) methods.push(1)

    // Determine recipient type from AI targeting
    let recipientType = 'groups'
    let selectedFloors = []
    if (d.floors && d.floors.length > 0) {
      recipientType = 'floors'
      selectedFloors = d.floors
    }

    setFormData(prev => ({
      ...prev,
      title: d.title || prev.title,
      content: d.htmlContent || prev.content,
      priority: priorityMap[d.priority] ?? prev.priority,
      eventDate: d.eventDate ? new Date(d.eventDate).toISOString().slice(0, 16) : prev.eventDate,
      scheduleDate: d.scheduleDate ? new Date(d.scheduleDate).toISOString().slice(0, 16) : prev.scheduleDate,
      isAcknowledgeReq: d.requiresAcknowledgment ?? prev.isAcknowledgeReq,
      notificationMethods: methods.length > 0 ? methods : prev.notificationMethods,
      isAllResidents: d.allResidents ?? prev.isAllResidents,
      isAllOwners: d.allOwners ?? prev.isAllOwners,
      isAllStaffs: d.allStaff ?? prev.isAllStaffs,
      recipientType,
      selectedFloors,
    }))
    setShowAIChat(false)
  }

  const handleAIRevise = async () => {
    setReviseLoading(true)
    setReviseError(null)

    try {
      const response = await announcementApi.reviseAI({ content: formData.content })

      if (response.data) {
        if (!response.data.result) {
          setReviseError(response.data.content || 'Failed to revise the announcement.')
          return
        }
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          content: response.data.content || prev.content
        }))
        setShowReviseModal(false)
      }
    } catch (err) {
      setReviseError(extractErrorMessage(err))
    } finally {
      setReviseLoading(false)
    }
  }

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'High'
      case 2: return 'Medium'
      case 3: return 'Low'
      default: return 'Medium'
    }
  }

  // Priority colors now handled by Badge component variants

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <DetailPageLayout
      title={isEditing ? 'Edit Announcement' : 'Create Announcement'}
      backLink={{ label: "Back to Announcements", href: "/announcements" }}
      actions={[
        <Button
          key="save"
          onClick={handleSubmit}
          disabled={submitting}
          variant="primary"
          icon={<Save className="h-4 w-4" />}
          loading={submitting}
        >
          {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
        </Button>
      ]}
    >

      <Card>
        <CardHeader
          title="Announcement Details"
          action={
            <div className="flex items-center space-x-2">
              <Button
                variant="primary"
                size="small"
                type="button"
                onClick={() => setShowAIChat(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white border-0"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                AI Generate
              </Button>
              {formData.content.trim() && (
                <Button
                  variant="primary"
                  size="small"
                  type="button"
                  onClick={() => setShowReviseModal(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-0"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  AI Revise
                </Button>
              )}
            </div>
          }
        />
        <CardBody className="space-y-6">
          <FormField
            label="Title"
            name="title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            error={errors.title}
            placeholder="Enter announcement title"
            maxLength={200}
            helpText={`${formData.title.length}/200 characters`}
            required
          />

          <FormTextarea
            label="Content"
            name="content"
            value={formData.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            error={errors.content}
            placeholder="Enter announcement content (supports HTML formatting)"
            rows={8}
            maxLength={5000}
            helpText={`${formData.content.length}/5000 characters`}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Building</label>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
                {selectedBuildingName || 'No building selected'}
              </div>
              {!selectedBuildingId && (
                <p className="mt-1 text-sm text-red-500">Please select a building from the top bar</p>
              )}
            </div>

            <FormSelect
              label="Priority"
              name="priority"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
              options={[
                { value: 1, label: 'High Priority' },
                { value: 2, label: 'Medium Priority' },
                { value: 3, label: 'Low Priority' }
              ]}
              helpText={`Current: ${getPriorityText(formData.priority)} Priority`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Event Date (Optional)"
              name="eventDate"
              type="datetime-local"
              value={formData.eventDate}
              onChange={(e) => handleInputChange('eventDate', e.target.value)}
              helpText="When the announced event will occur"
            />

            <FormField
              label="Schedule Date (Optional)"
              name="scheduleDate"
              type="datetime-local"
              value={formData.scheduleDate}
              onChange={(e) => handleInputChange('scheduleDate', e.target.value)}
              helpText="When to send the announcement"
            />
          </div>

          <FormGroup
            title="Notification Methods"
            description="Select how recipients will be notified"
          >
            <div className="space-y-2">
              <FormCheckbox
                label={(
                  <span className="flex items-center">
                    <Bell className="w-4 h-4 mr-1" />
                    Email
                  </span>
                )}
                name="email"
                checked={formData.notificationMethods.includes(1)}
                onChange={(e) => handleNotificationMethodChange(1, e.target.checked)}
              />
              <FormCheckbox
                label={(
                  <span className="flex items-center">
                    <Bell className="w-4 h-4 mr-1" />
                    SMS
                  </span>
                )}
                name="sms"
                checked={formData.notificationMethods.includes(2)}
                onChange={(e) => handleNotificationMethodChange(2, e.target.checked)}
              />
              <FormCheckbox
                label={(
                  <span className="flex items-center">
                    <Bell className="w-4 h-4 mr-1" />
                    Push Notification
                  </span>
                )}
                name="push"
                checked={formData.notificationMethods.includes(3)}
                onChange={(e) => handleNotificationMethodChange(3, e.target.checked)}
              />
            </div>
            {errors.notificationMethods && (
              <ErrorMessage message={errors.notificationMethods} />
            )}
          </FormGroup>

          <FormGroup
            title="Recipients"
            description="Choose who will receive this announcement"
          >
            <div className="space-y-4">
              <div className="flex gap-4">
                {['groups', 'floors', 'members'].map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="recipientType"
                      value={type}
                      checked={formData.recipientType === type}
                      onChange={() => { handleInputChange('recipientType', type); setRecipientCount(null) }}
                      className="text-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {type === 'groups' ? 'By Group' : type === 'floors' ? 'By Floor' : 'Specific Members'}
                    </span>
                  </label>
                ))}
              </div>

              {formData.recipientType === 'groups' && (
                <div className="space-y-2 ml-1">
                  <FormCheckbox
                    label="All Residents"
                    name="isAllResidents"
                    checked={formData.isAllResidents}
                    onChange={(e) => handleInputChange('isAllResidents', e.target.checked)}
                  />
                  <FormCheckbox
                    label="All Owners"
                    name="isAllOwners"
                    checked={formData.isAllOwners}
                    onChange={(e) => handleInputChange('isAllOwners', e.target.checked)}
                  />
                  <FormCheckbox
                    label="All Staff"
                    name="isAllStaffs"
                    checked={formData.isAllStaffs}
                    onChange={(e) => handleInputChange('isAllStaffs', e.target.checked)}
                  />
                </div>
              )}

              {formData.recipientType === 'floors' && (
                <div>
                  {availableFloors.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {availableFloors.map((floor) => {
                        const isSelected = formData.selectedFloors.includes(floor)
                        return (
                          <button
                            key={floor}
                            type="button"
                            onClick={() => handleFloorToggle(floor)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              isSelected
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
                            }`}
                          >
                            Floor {floor}
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {formData.buildingId ? 'No floors found for this building.' : 'Select a building first.'}
                    </p>
                  )}
                  {formData.selectedFloors.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      {formData.selectedFloors.length} floor{formData.selectedFloors.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              {formData.recipientType === 'members' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => handleMemberSearch(e.target.value)}
                    placeholder="Search members by name..."
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between gap-3">
                    {selectedMembers.length > 0 ? (
                      <p className="text-xs text-gray-500">{selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''} selected</p>
                    ) : <span />}
                    {membersTotal !== null && (
                      <p className="text-xs text-gray-500">
                        Showing {availableMembers.length} of {membersTotal}
                      </p>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {membersLoading ? (
                      <p className="p-3 text-sm text-gray-500 text-center">Loading members...</p>
                    ) : availableMembers.length > 0 ? (
                      availableMembers.map((member) => {
                        const isSelected = selectedMembers.some(m => m.userId === member.userId)
                        const displayName = [member.firstName, member.lastName].filter(Boolean).join(' ') || 'Unknown'
                        const role = member.isStaff ? 'Staff' : `Unit ${member.unitNumber || '?'}, Floor ${member.floor || '?'}`
                        return (
                          <button
                            key={member.userId}
                            type="button"
                            onClick={() => toggleMember(member)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${isSelected ? 'bg-primary-50' : ''}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-medium text-gray-600">
                              {(member.firstName?.[0] || '?').toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                              <p className="text-xs text-gray-500">{role}</p>
                            </div>
                            {isSelected && (
                              <svg className="w-5 h-5 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        )
                      })
                    ) : (
                      <p className="p-3 text-sm text-gray-500 text-center">
                        {formData.buildingId ? 'No members found.' : 'Select a building first.'}
                      </p>
                    )}
                  </div>
                  {membersTotal !== null && availableMembers.length < membersTotal && (
                    <button
                      type="button"
                      onClick={handleLoadMoreMembers}
                      disabled={membersLoadingMore}
                      className="w-full text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {membersLoadingMore ? 'Loading...' : 'Load more members'}
                    </button>
                  )}
                </div>
              )}

              {/* Calculate Recipients */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={calculateRecipients}
                  disabled={countLoading || !formData.buildingId}
                  className="text-sm px-4 py-2 rounded-lg border border-primary-300 text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {countLoading ? 'Calculating...' : 'Calculate Recipients'}
                </button>
                {recipientCount !== null && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-900">{recipientCount.total} recipient{recipientCount.total !== 1 ? 's' : ''}</span>
                    {recipientCount.residents != null && (
                      <span className="text-gray-500">
                        ({recipientCount.residents} residents, {recipientCount.owners} owners, {recipientCount.staff} staff)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </FormGroup>

          <FormGroup title="Additional Options">
            <div className="space-y-3">
              <FormCheckbox
                label="Require acknowledgment from recipients"
                name="isAcknowledgeReq"
                checked={formData.isAcknowledgeReq}
                onChange={(e) => handleInputChange('isAcknowledgeReq', e.target.checked)}
              />

              <FormCheckbox
                label="Save as template for future use"
                name="isTemplate"
                checked={formData.isTemplate}
                onChange={(e) => handleInputChange('isTemplate', e.target.checked)}
              />
            </div>
          </FormGroup>
        </CardBody>
      </Card>

      {/* Preview Section */}
      <Card>
        <CardHeader title="Preview" />
        <CardBody>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-medium text-gray-900">
                {formData.title || 'Untitled Announcement'}
              </h4>
              <div className="flex items-center space-x-2">
                <Badge
                  variant={formData.priority === 1 ? 'danger' :
                           formData.priority === 2 ? 'warning' : 'default'}
                >
                  {getPriorityText(formData.priority)} Priority
                </Badge>
                {formData.isAcknowledgeReq && (
                  <Badge variant="primary" className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    Acknowledgment Required
                  </Badge>
                )}
              </div>
            </div>
            {formData.content ? (
              <div
                className="text-gray-700 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: formData.content }}
              />
            ) : (
              <div className="text-gray-700">No content provided</div>
            )}
            {formData.eventDate && (
              <div className="mt-3 flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                Event: {new Date(formData.eventDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* AI Chat Panel */}
      {showAIChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl" style={{ height: '600px' }}>
            <AIChatPanel
              buildingId={formData.buildingId}
              onApply={handleAIApply}
              onClose={() => setShowAIChat(false)}
            />
          </div>
        </div>
      )}

      {/* AI Revise Modal */}
      <Modal
        isOpen={showReviseModal}
        onClose={() => { setShowReviseModal(false); setReviseError(null) }}
        title="Revise Announcement with AI"
        size="large"
      >
        <div className="space-y-4">
          {reviseError && (
            <ErrorMessage
              message={reviseError}
              onDismiss={() => setReviseError(null)}
            />
          )}

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              The AI will take your current content and reformat it into a professional, well-structured announcement with HTML formatting, without changing its core message.
            </p>
          </div>

          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
            <p className="text-xs text-gray-500 mb-1 font-medium">Current content:</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{formData.content}</p>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowReviseModal(false)
                setReviseError(null)
              }}
              disabled={reviseLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleAIRevise}
              disabled={reviseLoading}
              loading={reviseLoading}
            >
              Revise
            </Button>
          </div>
        </div>
      </Modal>
    </DetailPageLayout>
  )
}

export default CreateAnnouncement
